import React, {useState} from 'react';
import {ControlButton, TscButtonType, Glyphs} from '@techsmith/tsc-cloud-style-guide';
import SquareGlyph from '../../static/svg/SquareGlyph.svg';
import FreehandGlyph from '../../static/svg/FreehandGlyph.svg';
import ArrowGlyph from '../../static/svg/ArrowGlyph.svg';
import ColorPicker from './ColorPicker';
import {useTranslation} from 'react-i18next';
import Constants, {AnnotationModes, AnnotationColors, AnnotationTools} from '../../constants/Constants';
import Freehand from './AnnotationShapes/Freehand';
import Square from './AnnotationShapes/Square';
import Arrow from './AnnotationShapes/Arrow';
import config from '../../service/config';
import styled from 'styled-components';
import {canvasStore} from '../../context/canvasProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import {disableConversationStore} from '../../context/disableConversationProvider';
import {mediaStore} from '../../context/mediaProvider';
import {updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';
import {AlertModalPortal} from '../util/ModalPortalHelper';
import {Canvas} from 'fabric/fabric-impl';
import {importMediaInterface} from '../../hooks/dynamicImport';

const AnnotationToolBarWrapper = styled.div`
   display: flex;
`;

export const AnnotationToolBarBase: React.FC<IAnnotationToolBarProps & IStateMappedProps> = ({setAnnotationExists, canvasRef, annotationMode, annotationTool, isAnnotatingDisabled, IsVideo}) => {
   const {t} = useTranslation();
   const [showAlert, setShowAlert] = useState(false);

   const resetCanvasState = (): void => {
      if (!canvasRef) {
         return;
      }

      canvasRef.defaultCursor = 'pointer';
      canvasRef.isDrawingMode = false;
      canvasRef.off('mouse:down');
      canvasRef.off('mouse:move');
      canvasRef.off('mouse:up');
      canvasRef.off('path:created');
   };

   React.useEffect(() => {
      if (annotationTool === AnnotationTools.none) {
         resetCanvasState();
         return;
      }

      if (IsVideo) {
         void importMediaInterface().then(mediaInterface => {
            mediaInterface.pause();
         });
      }

      if (config.user.TechSmithId === Constants.nullTechSmithId) {
         updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: true});
      }

      startAnnotating(annotationTool);
   }, [annotationTool]);

   React.useEffect(() => {
      if (annotationMode !== AnnotationModes.edit && annotationTool !== AnnotationTools.none) {
         canvasRef.isDrawingMode = false;
         updateAnnotationManagerState({annotationTool: AnnotationTools.none});
      }
   }, [annotationMode]);

   const startAnnotating = (tool: AnnotationTools) => {
      resetCanvasState();

      if (tool === AnnotationTools.arrow) {
         Arrow.turnOnMouseEventListeners(canvasRef, setAnnotationExists);
      } else if (tool === AnnotationTools.square) {
         Square.turnOnMouseEventListeners(canvasRef, setAnnotationExists);
      } else if (tool === AnnotationTools.freehand) {
         Freehand.startDrawing(canvasRef, setAnnotationExists);
      }
   };

   const onClickTool = (tool: AnnotationTools) => {
      if (tool === annotationTool) {
         updateAnnotationManagerState({annotationTool: AnnotationTools.none});
      } else {
         updateAnnotationManagerState({annotationTool: tool, annotationMode: AnnotationModes.edit});
      }
   };

   const onColorChange = (color: AnnotationColors): void => {
      updateAnnotationManagerState({annotationColor: color});
   };

   const clearCanvas = (): void => {
      updateAnnotationManagerState({annotationMode: AnnotationModes.none, annotationTool: AnnotationTools.none});
      resetCanvasState();
      setShowAlert(false);
      setAnnotationExists(false);
   };

   const onCancelDeletion = (): void => {
      setShowAlert(false);
   };

   const deleteAnnotations = (): void => {
      if (canvasRef.getObjects().length) {
         setShowAlert(true);
      } else {
         clearCanvas();
      }
   };

   const isToolDisabled = isAnnotatingDisabled || !canvasRef;
   const isSquareActive = annotationTool === AnnotationTools.square && annotationMode === AnnotationModes.edit;
   const isArrowActive = annotationTool === AnnotationTools.arrow && annotationMode === AnnotationModes.edit;
   const isFreehandActive = annotationTool === AnnotationTools.freehand && annotationMode === AnnotationModes.edit;

   return (
      <AnnotationToolBarWrapper>
         <ControlButton
            buttonType={TscButtonType.destructive}
            isFullWidth={false}
            onClick={deleteAnnotations}
            title={t('annotation.exitAnnotationMode')}
            testId="annotation-clear-button"
            glyph={<Glyphs.Close16x16 />}
            disabled={!canvasRef}
            visible={annotationMode === AnnotationModes.edit}
         />
         {showAlert && (
            <AlertModalPortal
               title={t('annotation.clearAnnotation')}
               onClose={onCancelDeletion}
               closeContent={t('general.cancel')}
               onConfirm={clearCanvas}
               confirmContent={t('general.clear')}
               confirmButtonType={TscButtonType.destructive}
               onDismiss={onCancelDeletion}
               dismissContent={t('general.cancel')}
               testId="clear-canvas-alert"
            >
               <div>
                  {t('annotation.confirmCanvasDeletionText')}
               </div>
            </AlertModalPortal>
         )}
         <ControlButton
            className={isSquareActive ? 'active-annotation-tool' : ''}
            buttonType={TscButtonType.secondary}
            isFullWidth={false}
            onClick={() => onClickTool(AnnotationTools.square)}
            title={t('annotation.square')}
            testId="annotation-square-button"
            glyph={<SquareGlyph />}
            active={isSquareActive}
            aria={{'aria-pressed': isSquareActive}}
            disabled={isToolDisabled}
         />
         <ControlButton
            className={isArrowActive ? 'active-annotation-tool' : ''}
            buttonType={TscButtonType.secondary}
            isFullWidth={false}
            onClick={() => onClickTool(AnnotationTools.arrow)}
            title={t('annotation.arrow')}
            testId="annotation-arrow-button"
            glyph={<ArrowGlyph />}
            active={isArrowActive}
            aria={{'aria-pressed': isSquareActive}}
            disabled={isToolDisabled}
         />
         <ControlButton
            className={isFreehandActive ? 'active-annotation-tool' : ''}
            buttonType={TscButtonType.secondary}
            isFullWidth={false}
            onClick={() => onClickTool(AnnotationTools.freehand)}
            title={t('annotation.freehand')}
            testId="annotation-freehand-button"
            glyph={<FreehandGlyph />}
            active={isFreehandActive}
            aria={{'aria-pressed': isSquareActive}}
            disabled={isToolDisabled}
         />
         <ColorPicker onColorChange={onColorChange} />
      </AnnotationToolBarWrapper>
   );
};

export interface IAnnotationToolBarProps {
   setAnnotationExists: (annotationExists: boolean) => void;
}

export interface IStateMappedProps {
   canvasRef: Canvas;
   annotationMode: AnnotationModes;
   annotationTool: AnnotationTools;
   isAnnotatingDisabled: boolean;
   IsVideo: boolean;
}

const mapStatesToProps = (
   {setAnnotationExists}: IAnnotationToolBarProps,
   {canvasRef}: Partial<IStateMappedProps>,
   {annotationMode, annotationTool}: Partial<IStateMappedProps>,
   {isAnnotatingDisabled}: Partial<IStateMappedProps>,
   {IsVideo}: Partial<IStateMappedProps>
): IAnnotationToolBarProps & IStateMappedProps => ({setAnnotationExists, canvasRef, annotationMode, annotationTool, isAnnotatingDisabled, IsVideo});

export default withMemoizedContexts(mapStatesToProps, canvasStore, annotationManagerStore, disableConversationStore, mediaStore)(AnnotationToolBarBase);
